/*****************************************************************************
 *   © Copyright 2021   Quantitative Software Management, Inc.               *
 *              --  All rights reserved.  --                                 *
 *   Unpublished -- rights reserved under the Copyright Laws                 *
 *   of the United States.                                                   *
 *                                                                           *
 *   This computer software is protected by trade secret, copyright law,     *
 *   and international treaties and contains proprietary and confidential    *
 *   trade secrets of Quantitative Software Management, Inc.  Unauthorized   *
 *   use, reproduction, or distribution of this program or any portion of    *
 *   it will result in severe civil or criminal penalties and will be        *
 *   prosecuted to the full extent of the law.                               *
 *                                                                           *
 *   Use, duplication, or disclosure by the Government is subject to         *
 *   restrictions as set forth in                                            *
 *      (i) a license agreement incorporated in the contract,                *
 *      (ii) the definition of "restricted rights" in paragraph (c)(1)(ii)   *
 *           of DFARS 252.227-7013 (1988), and                               *
 *      (iii) the definition of "restricted rights" in paragraph (a) of      *
 *            FAR 52.227-14, Alt III (Jun 1987) or                           *
 *      (iv) FAR 52.227-19 (Jun 1987), as applicable.                        *
 *   For purposes of the FAR, this software shall be deemed to               *
 *   be "unpublished" and licensed with disclosure prohibitions.             *
 *   Contractor/Manufacturer is   Quantitative Software Management, Inc.     *
 *                                                                           *
 *****************************************************************************/

import { ChartType, DashboardType, MetricRatingMode, Stage, TimeSlice } from "../app.enums";
import { DTOBase } from "./DTOBase";
import { MetricInfo } from './MetricInfo';

export class DashboardPart {
  public AdvancedSettings: string = null;
  public ChartOptions: number = null;
  public ChartType: ChartType = null;
  public Metrics: any[] = [];
  public ZoneId: string = null;
  public Size: ChartSize = null;
  public Id: number = 0;
  public PartId: string = null;
  public TypeName: string = null;
  public ContingencyProfileId: number | null = null;

  constructor(dpart: DashboardPart = null) {
    this.PartId = dpart?.PartId;
    this.ZoneId = dpart?.ZoneId;
  }
}

export class DashboardInfo extends DTOBase {
  public DashboardType: DashboardType;
  public Description: string;
  public Id: number;
  public Owner: number;
  public PartsList: DashboardPart[] = [];
  public Shared: boolean;
  public Temporary: boolean;
  public Title: string = null;
  public TypeName: string;
  public SortOrder: number = null;
  public FolderId: number = null;
}

export class xyData {
  public x: number | null;
  public y: Date | number | null;
  public name: string;

  constructor(solname: string, xval: number | null, yval: number | null) { this.name = solname, this.x = xval, this.y = yval; }
}

export class ProbabilityData {
  public solData: xyData[];
  public seriesName: string;
  public unit: string;

  constructor(seriesname: string, data: xyData[], unit: string) {
    this.seriesName = seriesname;
    this.unit = unit;
    this.solData = [];  //clear it
    for (let i = 0; i < data.length; i++) {
      this.solData.push(data[i]);
    }
  }
}

export class TrendSolData {
  public solName: string;
  public solContext: number;
  public x: number | null;
  public y: number | null;
  public tooltip: string;
  public deviation: number;
  public sigmas: number;
  public diffString: string;
}

export class TrendData {
  public solData: TrendSolData[];
  public seriesName: string;
}

export class TrendArea {
  public xVals: number[];
  public yHiVals: number[];
  public yLoVals: number[];
}

export class TrendChartData {
  public TrendName: string = "";
  public DepMetricName: string = "";
  public DepMetricDecimals: number = 1;
  public DataCount: number = 0;
  public IndMetricName: string = "";
  public IsReversed: boolean = false;
  public list: TrendData[] = [];
  public lines: TrendData[] = [];
  public areas: TrendArea[] = [];
  public xAxisIsLog: boolean = false;
  public yAxisIsLog: boolean = false;
  public OverallAssessment: number | null = null;
  public AssessmentTooltip: string = null;
}

export class TrendGridData {
  public deviation: number | null;
  public sigmas: number | null;
  public solContext: number | null;
  public solName: string;
  public tooltip: string;
  public x: number | null;
  public y: number | null;
}

//use for Solution Comparison Charts (see EstimationService.GetSolutionComparisonData )
export class SolutionComparisonData {
  public Id: number;
  public Name: string;
  public Description: string;
  public Value: number | null;
  public SolutionContext: number;    //enum      
}

//use for Portfolio Gantt charts; see DataManager.GanttDataForProjects
export class GanttData extends DTOBase {  //: DTOBase
  public Id: number;
  public Name: string;
  public StartDate: Date | null;
  public EndDate: Date | null;
  public ProjectStage: number;    //enum  ProjectStage

  constructor(data: GanttData = null) {
    super();
    this.Id = data?.Id;
    this.Name = data?.Name;
    this.StartDate = new Date(data?.StartDate);
    this.EndDate = new Date(data?.EndDate);
    this.ProjectStage = data?.ProjectStage;
  }
}

//use for Portfolio time series charts; see PortfolioManager.GetProjectTimeSeriesData
//use for Estimation skill charts;  see TimeSeriesManager.GetSkillTimeSeries
//not used for Estimation and Contingency time series charts; see TimeSeriesChart.GetSolutionTimeSeriesData
export class TimeSeries {
  public items: TimeSeriesData[] = [];
  public name: string;
  public start: Date;
  public end: Date;

  constructor(data: TimeSeries = null) {
    if (data) {
      this.start = new Date(data.start);
      this.end = new Date(data.end);
      this.items = data.items.map(x => { return new TimeSeriesData(x.yValue, x.date); });
      this.name = data.name;
    }
    else {
      this.items = [];
    }
  }
}

export class TimeSeriesData {
  public yValue: number | null;
  public date: Date;

  constructor(val: number | null = null, d: Date = null) {
    this.yValue = val;
    this.date = d ? new Date(d) : null;
  }
}

export class TimeSeriesChartData {
  public Metric: MetricInfo = new MetricInfo();
  public seriesData: TimeSeries[] = [];
  public vAxisMax: number = 0;
  public vAxisMin: number = 0;
  public dateMin: Date = new Date();
  public dateMax: Date = new Date();
  public IsValid: boolean = true;
  public timeSlice: TimeSlice = TimeSlice.None;

  constructor(data: TimeSeriesChartData = null) {
    if (data) {
      this.Metric = data.Metric;
      this.vAxisMin = data.vAxisMin;
      this.vAxisMax = data.vAxisMax;
      this.dateMin = new Date(data.dateMin);
      this.dateMax = new Date(data.dateMax);
      this.IsValid = data.IsValid;
      this.timeSlice = data.timeSlice;
      //this.seriesData = JSON.parse(JSON.stringify(data.seriesData)) as TimeSeries[];   // (deep) copy the array
      this.seriesData = data.seriesData.map((series) => {
        return new TimeSeries(series);
      });
    }
  }
}

export class SkillsTimeSeriesData extends TimeSeriesChartData {
  public Skills: Skill[] = []
  constructor(data: SkillsTimeSeriesData = null) {
    super(data as TimeSeriesChartData);
    if (data) {
      this.Skills = JSON.parse(JSON.stringify(data.Skills)) as Skill[];
    }
  }
}

export class MetricAssessmentInfo {
  public metricId: number;
  public metricName: string;
  public metricUnit: string;
  public decimals: number;
  public ratingMode: MetricRatingMode;
  public stars: number;
  public percentile: number;
  public percentileDisplayString: string;
  public deviation: number;
  public sigmas: number;
  public sortOrder: number;
  //public bool active;
  public solutionX: number | null;
  public solutionY: number | null;
  public trendY: number | null;
  public diff: number;
  public diffPercent: number;
  public trendGroupName: string = '';
}

///use for Estimation (and contingency?) Milestone reports
export class MilestoneGridItem {
  public Acronym: string;
  public Name: string;
  public Date1: Date | null;
  public Date2: Date | null;
  public Diff: number;
}

// for portfolio histograms, pie charts (see PortfolioManager.GetDistributionData)
export class DistributionData extends DTOBase {
  public Id: number;
  public txtData: string;
}

export class PieSlice {
  public Category: string;
  public Count: number | null;
  public Percentage: number | null;
}

// for portfolio histograms (see PortfolioManager.GetHistogramData, ChartManager.GetHistogramData)
export class BarLine {
  public categoryName: string;
  public avgMetricValue: number | null;
}

//all summary reports
export class MetricComparisonItem {
  public Metric: string;
  public CurrentValue: string;
  public ComparisonValue: string;
}

export class ProjectTimeSeriesGridData {
  public Date: Date | null;
  public ProjectMonth: number;
  public SolutionValue: number;
  public ComparisonSolutionValue: number;
}

export class RiskData {
  public Active: boolean;
  public Name: string;
  public UserSolValue: number | null;
  public UserSolRatingText: string;
  public UserSolRating: number | null;
  public Difference: number | null;
  public Unit: string;
  public CompIndMetricValue: number | null;
  public CompSolRating: number | null;
  public CompSolRatingText: string;
  public CompSolValue: number | null;
  public Decimals: number | null;
  public Id: number | null;
  public UserIndMetricValue: number | null;
  public indName: string;
}

export class SizeDetailData {
  public ComponentName: string;
  public ComponentCount: number | null;
  public FUPerComponent: number | null;
  public LineTotal: number | null;

  constructor(data: SizeDetailData = null) {
    this.ComponentName = data?.ComponentName;
    this.ComponentCount = data?.ComponentCount;
    this.FUPerComponent = data?.FUPerComponent;
    this.LineTotal = data?.LineTotal;
    }
}

export class SizeDetails {
  public solutionId: number;
  public solutionName: string;
  public sizeMethod: string;
  public sizeTotal: number | null = null;
  public sizeEffective: number | null = null;
  public sizeDetailConfigName: string;
  public sizeDetail: SizeDetailData[] = [];

  constructor(data: SizeDetails = null) {
    this.solutionId = data?.solutionId;
    this.solutionName = data?.solutionName;
    this.sizeMethod = data?.sizeMethod;
    this.sizeTotal = data?.sizeTotal;
    this.sizeEffective = data?.sizeEffective;
    this.sizeDetailConfigName = data?.sizeDetailConfigName ?? '';
    this.sizeDetail = [];
    if (data && data.sizeDetail?.length > 0) {
      data.sizeDetail.forEach((item) => {
        this.sizeDetail.push(new SizeDetailData(item));
      });
    }
  }
}

export class SkillsBreakoutData {
  public Context: number | null;
  public Description: string;
  public Id: number | null;
  public Name: string;
  public Value: number | null;
}

export enum ChartSize {
  Normal = 0,
  Thumbnail = 10,
  Small = 20,
  Medium = 30,
  Large = 40,
  Zoom = 100,
  Print = 200,
  Ppt = 300
}

export enum ChartChangeTypes {
  Ready,
  Zoom,
  UnZoom,
  Modified,
  Removed,
  Save,
  Duplicate,
  PPTExportCompleted,
  ExportError,
  PdfExport,
  ExcelExport
}

export enum DashboardChangeTypes {
  Modified,
  UnModified,
  Saved,
  ChangeStrategy,
  ReturnFromManagement
}

export class QuadrantExtraData {
  public showTargetArea: boolean = true;
  public targetHorMin: number | null = -1;
  public targetHorMax: number | null = 1;
  public targetVertMin: number | null = -1;
  public targetVertMax: number | null = 1;
  public descBottomLeft: string = null;
  public descBottomRight: string = null;
  public descTopLeft: string = null;
  public descTopRight: string = null;

  public get targetAreaIsNull(): boolean {
    return this.targetHorMin == null || this.targetHorMax == null || this.targetVertMin == null || this.targetVertMax == null;
  }

  public constructor() {
    this.showTargetArea = true;
    this.targetHorMin = -1;
    this.targetHorMax = 1;
    this.targetVertMin = -1;
    this.targetVertMax = 1;
    this.descBottomLeft = '';
    this.descBottomRight = '';
    this.descTopLeft = '';
    this.descTopRight = '';
  }
}

export class QuadrantData extends DTOBase {
  public Id: number;
  public projectId: number;
  public projectName: string;
  public projectStage: Stage;
  public xValue: number;
  public yValue: number;
  public bubbleValue: number | null;
  public get yValues(): number[] {
    //yValues cannot be null in the bubble chart, so we handle legitimate null bubble values by drawing them the nominal size (but labeling correctly in the tooltip)
    let z = [];
    z.push(this.yValue);
    z.push(this.bubbleValue == null ? 0 : this.bubbleValue);
    return z;
  }
  constructor(data: QuadrantData = null) {
    super();
    this.Id = data?.Id;
    this.projectId = data?.projectId;
    this.projectName = data?.projectName;
    this.projectStage = data?.projectStage;
    this.xValue = data?.xValue;
    this.yValue = data?.yValue;
    this.bubbleValue = data?.bubbleValue;
  }
}

export class QuadrantSeries {
  public name: string;
  public data: QuadrantData[];
}

export class PortfolioFiveStarData extends DTOBase {
  public project: string;
  public Id: number;
  public metric1Name: string;
  public metric1Value: (number | null) = null;
  public metric2Name: string;
  public metric2Value: (number | null) = null;
  public metric3Name: string;
  public metric3Value: (number | null) = null;
  public metric4Name: string;
  public metric4Value: (number | null) = null;
  public metric5Name: string;
  public metric5Value: (number | null) = null;

  constructor(data: PortfolioFiveStarData = null) {
    super();
    this.project = data?.project;
    this.Id = data?.Id;
    this.metric1Name = data?.metric1Name;
    this.metric1Value = data?.metric1Value;
    this.metric2Name = data?.metric2Name;
    this.metric2Value = data?.metric2Value;
    this.metric3Name = data?.metric3Name;
    this.metric3Value = data?.metric3Value;
    this.metric4Name = data?.metric4Name;
    this.metric4Value = data?.metric4Value;
    this.metric5Name = data?.metric5Name;
    this.metric5Value = data?.metric5Value;
  }
}

export class PortfolioFiveStarChartData {
  public trendName: string;
  public data: PortfolioFiveStarData[] = [];
}

export class DashboardFolder {
  public Id: number;
  public FolderId: number;
  public Title: string;
  public Owner: number;
  public DashboardList: any[];
  public Shared: boolean;
  public SortOrder: number;
  public DashboardType: number;
  public items: DashboardInfo[] = [];
  public Locked?: boolean = false;
  constructor(data: DashboardFolder = null) {
    this.Id = data?.Id;
    this.FolderId = data?.FolderId;
    this.Title = data?.Title;
    this.Owner = data?.Owner;
    this.DashboardList = data?.DashboardList;
    this.Shared = data?.Shared;
    this.SortOrder = data?.SortOrder;
    this.DashboardType = data?.DashboardType;
    this.items = data?.items;
    this.Locked = data?.Locked;
  }
}

export class Skill {
  public Id: number;
  public Name: string;
  public Acronym: string;
  public ExternalId: string;
  public LaborRate: number;

  constructor(data: Skill = null) {
    this.Id = data?.Id;
    this.Name = data?.Name;
    this.Acronym = data?.Acronym;
    this.ExternalId = data?.ExternalId;
    this.LaborRate = data?.LaborRate;
  }
}
